import { type Block, BlockSchema, type BlockType, newBlockId } from "@aurumiq/cms-schema";
import type { LucideIcon } from "lucide-react";
import {
  CircleHelp,
  Clapperboard,
  Grid3X3,
  Images,
  LayoutGrid,
  Megaphone,
  MessageSquareQuote,
  PanelTop,
  Sparkles,
  Tags,
  Type,
} from "lucide-react";

/**
 * Starter content per block type. Only the fields that carry copy are listed; everything
 * with a schema default (style, hidden, align, …) is filled by parsing through cms-schema.
 * Media refs point at `placeholder` — the designer's replace-image flow swaps them out.
 */
const PLACEHOLDER_IMAGE = { id: "placeholder", alt: "Placeholder image" };

const STARTERS: Record<BlockType, Record<string, unknown>> = {
  hero: {
    eyebrow: "New",
    headline: "Intelligent gold invoicing",
    subheadline: "Issue compliant invoices, verify purity and keep every gram accounted for.",
    primary_cta: { label: "Start free trial", href: "/pricing" },
    secondary_cta: { label: "Watch the demo", href: "/#video", variant: "secondary" },
  },
  stats: {
    heading: "Proof, not promises",
    items: [
      {
        value: "5",
        label: "verdicts a scan can return",
        detail: "VALID, CANCELLED, TAMPERED, INVALID CODE, NOT FOUND",
      },
      { value: "0", label: "internet needed at the counter" },
      { value: "< 2 s", label: "from scan to verdict" },
    ],
  },
  steps: {
    heading: "How it works",
    items: [
      {
        icon: "receipt",
        title: "Issue",
        body: "Pick the customer, add lines by SKU; totals follow the live gold rate.",
      },
      {
        icon: "qr-code",
        title: "Sign",
        body: "Every document gets an HMAC-signed QR and a security code.",
      },
      {
        icon: "shield-check",
        title: "Verify",
        body: "Anyone scans the QR and sees VALID, CANCELLED or TAMPERED.",
      },
    ],
  },
  features: {
    heading: "Everything a gold counter needs",
    intro: "Built for traders, jewellers and refiners.",
    items: [
      {
        icon: "scale",
        title: "Weigh and price",
        body: "Live rates, purity math and VAT in one tap.",
      },
      {
        icon: "shield",
        title: "Verify pages",
        body: "Every invoice gets a public verification page.",
      },
      {
        icon: "cloud",
        title: "Sync anywhere",
        body: "Works offline; syncs when you are back online.",
      },
    ],
  },
  screenshots: {
    heading: "See it in action",
    items: [{ image: PLACEHOLDER_IMAGE, caption: "The invoice screen" }],
  },
  video: {
    heading: "Two-minute tour",
    provider: "youtube",
    src: "dQw4w9WgXcQ",
  },
  pricing: {
    heading: "Simple pricing",
    intro: "Start free; upgrade when the counter gets busy.",
    plan_codes: ["starter"],
  },
  faq: {
    heading: "Frequently asked questions",
    items: [
      { question: "Does it work offline?", answer: "Yes. Invoices queue and sync when connected." },
      { question: "Is my data in the UAE?", answer: "Data is stored in the region you choose." },
    ],
  },
  cta: {
    headline: "Ready to modernise your counter?",
    body: "Set up in minutes. No card required.",
    primary_cta: { label: "Get started", href: "/pricing" },
  },
  logos: {
    heading: "Trusted by traders across the Gold Souk",
    items: [{ image: PLACEHOLDER_IMAGE, name: "Your customer" }],
  },
  testimonial: {
    heading: "What customers say",
    items: [
      {
        quote: "We cut invoicing time in half and our auditors love the verify pages.",
        author: "A. Rahman",
        role: "Owner, Rahman Jewellers",
      },
    ],
  },
  richtext: {
    html: "<h2>Section heading</h2><p>Write your copy here.</p>",
  },
  freeform: {
    elements: [
      { id: "el-heading", type: "text", x: 1, y: 1, w: 6, h: 2, html: "<h2>Free layout</h2>" },
      { id: "el-button", type: "button", x: 1, y: 3.5, w: 3, h: 1, label: "Learn more", href: "/" },
    ],
  },
};

/** A schema-valid block of `type` with starter content; `id` is fresh unless given. */
export function makeBlock(type: BlockType, id = newBlockId(type)): Block {
  return BlockSchema.parse({ id, type, ...STARTERS[type] });
}

export interface PaletteEntry {
  type: BlockType;
  label: string;
  description: string;
  icon: LucideIcon;
}

/** Add-panel entries, in the order they are offered. */
export const PALETTE: readonly PaletteEntry[] = [
  {
    type: "hero",
    label: "Hero",
    description: "Headline, sub-copy and calls to action",
    icon: PanelTop,
  },
  { type: "stats", label: "Stats", description: "Large figures with labels", icon: Sparkles },
  { type: "steps", label: "How it works", description: "Numbered step sequence", icon: LayoutGrid },
  { type: "features", label: "Features", description: "Icon cards in 2–4 columns", icon: Grid3X3 },
  {
    type: "screenshots",
    label: "Screenshots",
    description: "Captioned product images",
    icon: Images,
  },
  {
    type: "video",
    label: "Video",
    description: "YouTube, Vimeo or an uploaded file",
    icon: Clapperboard,
  },
  {
    type: "pricing",
    label: "Pricing",
    description: "Plan cards from the pricing tiers",
    icon: Tags,
  },
  { type: "faq", label: "FAQ", description: "Expandable questions and answers", icon: CircleHelp },
  {
    type: "cta",
    label: "Call to action",
    description: "Closing band with buttons",
    icon: Megaphone,
  },
  { type: "logos", label: "Logos", description: "Customer or partner logo strip", icon: Sparkles },
  {
    type: "testimonial",
    label: "Testimonials",
    description: "Quotes with author and role",
    icon: MessageSquareQuote,
  },
  { type: "richtext", label: "Rich text", description: "Free-form HTML copy", icon: Type },
  {
    type: "freeform",
    label: "Free layout",
    description: "Drag elements anywhere on a 12-column grid",
    icon: LayoutGrid,
  },
];

export const PALETTE_BY_TYPE: Record<string, PaletteEntry> = Object.fromEntries(
  PALETTE.map((p) => [p.type, p]),
);
